/* global BigInt */
import { assertSportsbookHistoryDetail } from "../contracts/sportsbookHistoryResponse.contract.js";

export const SPORTSBOOK_CANCEL_READ_POLICY = Object.freeze({ attempts: 6, intervalMs: 1000, deadlineMs: 6000 });
const abortError = () => new DOMException("Aborted", "AbortError");
const version = (value) => {
  if (typeof value !== "string" || !/^[1-9]\d{0,18}$/.test(value)) throw new Error("Invalid history version");
  return BigInt(value);
};

export const assertSportsbookHistoryCancelAcknowledgement = (response, row, command) => {
  if (response?.history_id !== row.history_id || response?.command !== "CANCEL"
    || !["ACCEPTED", "REPLAYED"].includes(response?.command_status)
    || response?.idempotency_key !== command.idempotency_key
    || typeof response?.projection_pending !== "boolean") {
    throw new Error("취소 응답을 확인하지 못했습니다. 내역을 확인해 주세요.");
  }
  return response;
};

const retryableReadError = (error) => {
  if (error?.name === "SportsbookHistoryContractError") return false;
  const response = error?.response;
  const status = response?.httpStatus ?? response?.status ?? error?.statusCode;
  return status === undefined || (typeof status === "number" && status >= 500 && status <= 599);
};

// A successful command is never repeated here. Only its authenticated detail is read.
export const waitForSportsbookHistoryCancellation = async ({
  row, requestDetail, signal,
  timers = { setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (id) => clearTimeout(id) },
}) => {
  if (signal?.aborted) throw abortError();
  const startingVersion = version(row.row_version);
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  let rejectAbort;
  const interrupted = new Promise((_, reject) => { rejectAbort = reject; });
  const rejectInterrupted = () => rejectAbort(abortError());
  controller.signal.addEventListener("abort", rejectInterrupted, { once: true });
  const deadline = timers.setTimeout(abort, SPORTSBOOK_CANCEL_READ_POLICY.deadlineMs);
  let delayTimer;
  const pause = () => new Promise((resolve) => {
    delayTimer = timers.setTimeout(() => { delayTimer = undefined; resolve(); }, SPORTSBOOK_CANCEL_READ_POLICY.intervalMs);
  });
  const read = async () => {
    for (let attempt = 0; attempt < SPORTSBOOK_CANCEL_READ_POLICY.attempts; attempt += 1) {
      if (controller.signal.aborted) throw abortError();
      let detail;
      let received = false;
      try {
        detail = await requestDetail(row.history_id, controller.signal);
        received = true;
      } catch (error) {
        if (controller.signal.aborted) throw abortError();
        if (!retryableReadError(error)) return { status: "pending", reason: "read-rejected" };
      }
      if (controller.signal.aborted) throw abortError();
      if (received) {
        try {
          assertSportsbookHistoryDetail(detail);
          if (detail.history_id !== row.history_id || version(detail.row_version) < startingVersion) {
            return { status: "pending", reason: "invalid-detail" };
          }
          if (detail.status === "CANCELLED") {
            if (version(detail.row_version) <= startingVersion || detail.capabilities.can_cancel !== false) {
              return { status: "pending", reason: "invalid-detail" };
            }
            return { status: "confirmed", detail };
          }
        } catch {
          return { status: "pending", reason: "invalid-detail" };
        }
      }
      if (attempt + 1 < SPORTSBOOK_CANCEL_READ_POLICY.attempts) await pause();
    }
    return { status: "pending", reason: "attempts-exhausted" };
  };
  try {
    return await Promise.race([read(), interrupted]);
  } catch (error) {
    if (signal?.aborted) throw abortError();
    if (controller.signal.aborted) return { status: "pending", reason: "deadline" };
    throw error;
  } finally {
    timers.clearTimeout(deadline);
    if (delayTimer !== undefined) timers.clearTimeout(delayTimer);
    signal?.removeEventListener("abort", abort);
    controller.signal.removeEventListener("abort", rejectInterrupted);
    controller.abort();
  }
};
